import React, { useState, useEffect } from 'react';
import { 
  ShieldAlert, Download, Copy, Check, 
  AlertCircle, FileText, ArrowLeft, RefreshCw, 
  Search, Sparkles, Building, UserCheck
} from 'lucide-react';

interface AmlAuditorProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const LOADING_STEPS_UK = [
  "Ініціалізація модуля комплаєнс-моніторингу...",
  "AML-офіцер сканує базу санкцій РНБО України за кодами та назвами...",
  "OSINT-аналітик шукає інформацію про бенефіціарів та зв'язки з РФ/РБ...",
  "Аналіз репутаційного фону та згадок у публічних джерелах...",
  "Координатор комплаєнс-контролю розраховує рівень ризику та формує фінальний звіт Due Diligence..."
];

const LOADING_STEPS_EN = [
  "Initializing compliance monitoring AI agents...",
  "AML Officer is scanning the NSDC Ukraine sanctions database...",
  "OSINT Analyst is identifying ultimate beneficiaries and links to RU/BY...",
  "Analyzing reputational background in public media sources...",
  "Compliance Coordinator is calculating the risk score and compiling the final Due Diligence report..."
];

export default function AmlAuditor({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: AmlAuditorProps) {
  const isUk = language === 'uk';
  const [querySubject, setQuerySubject] = useState('');
  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [amlReport, setAmlReport] = useState<string | null>(null);
  const [copiedReport, setCopiedReport] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);

  // Cycle loading steps
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 6000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk]);

  const handleRunAudit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!querySubject.trim()) return;

    setIsAuditing(true);
    setError(null);
    setAmlReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/audit-sanctions', {
        method: 'POST',
        headers,
        body: JSON.stringify({ querySubject })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка аналізу санкцій.' : 'Failed to audit sanctions.'));
      }

      const data = await response.json();

      if (data.report) {
        setAmlReport(data.report);
        setIsAuditing(false);
        return;
      }

      if (data.jobId) {
        const jobId = data.jobId;
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${jobId}`);
            if (statusRes.ok) {
              const statusData = await statusRes.json();
              if (statusData.status === 'completed') {
                clearInterval(pollInterval);
                setAmlReport(statusData.report);
                setIsAuditing(false);
              } else if (statusData.status === 'failed') {
                clearInterval(pollInterval);
                setError(statusData.error || (isUk ? 'Помилка аналізу санкцій.' : 'Failed to audit sanctions.'));
                setIsAuditing(false);
              }
            }
          } catch (pollErr) {
            console.error('Polling error:', pollErr);
          }
        }, 4000);
      } else {
        setIsAuditing(false);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка під час санкційного скринінгу.' : 'An error occurred during sanctions screening.'));
      setIsAuditing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!amlReport) return;

    try {
      const name = 'aml-report-' + querySubject.trim().replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, '_');
      
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: amlReport,
          fileName: name
        })
      });

      if (!response.ok) throw new Error(isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word file.');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    }
  };

  const handleCopyReport = () => {
    if (!amlReport) return;
    navigator.clipboard.writeText(amlReport);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 pb-12 text-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              🏠 {isUk ? "На головну" : "Home"}
            </button>
            <button
              onClick={onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4 text-blue-600" />
              <span>{isUk ? "← До Бізнес Центру" : "← Back to Business Hub"}</span>
            </button>
          </div>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-red-500" />
              <span>{isUk ? "AML & Санкційний Аудитор" : "AML & Sanctions Auditor"}</span>
            </h1>
            <p className="text-xs text-slate-500">
              {isUk ? "Скринінг контрагентів за базами РНБО та OSINT-аналіз зв'язків з агресором" : "Counterparty screening in NSDC database and OSINT analysis"}
            </p>
          </div>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 px-3 py-1 rounded-full flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5 text-red-500 animate-pulse" />
          <span className="text-[10px] text-red-500 font-bold uppercase tracking-wider">Multi-Agent AI</span>
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">{isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isUk ? "База знань компанії" : "Company Knowledge Base")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            {isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Main UI */}
      {!amlReport && !isAuditing ? (
        <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 space-y-6 max-w-2xl mx-auto mt-6 shadow-xs">
          <div className="text-center space-y-2">
            <div className="w-12 h-12 bg-red-50/50 border border-red-200 rounded-xl flex items-center justify-center mx-auto">
              <ShieldAlert className="w-6 h-6 text-red-500" />
            </div>
            <h2 className="text-base font-bold text-slate-800">
              {isUk ? "Скринінг надійності контрагента" : "Counterparty Verification"}
            </h2>
            <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
              {isUk 
                ? "Введіть код ЄДРПОУ/ІПН або назву підприємства (чи ПІБ директора), щоб перевірити суб'єкта по базах санкцій по базах санкцій та проаналізували непрямі AML-ризики." 
                : "Enter code, company name or person name to run an automated sanctions & AML risk check."}
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              setQuerySubject("42206417 ТОВ Альфа Трейдинг Груп");
              setError(null);
            }}
            className="w-full py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
          >
            <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
            <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (ЄДРПОУ 42206417)" : "⚡ Try 1-click Demo Sample (EDRPOU 42206417)"}</span>
          </button>

          <form onSubmit={handleRunAudit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                {isUk ? "Контрагент для перевірки" : "Target Subject / Entity"}
              </label>
              <div className="relative">
                <input 
                  type="text"
                  placeholder={isUk ? "Наприклад: 42206417 або ТОВ Магістар-СГ" : "e.g. 42206417 or Magistar-SG"}
                  value={querySubject}
                  onChange={(e) => setQuerySubject(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-red-500 focus:ring-1 focus:ring-red-500/30 text-sm text-slate-800 placeholder-slate-400 focus:outline-none transition-colors pr-10 font-medium"
                />
                <Search className="w-4 h-4 text-slate-400 absolute right-3 top-3.5" />
              </div>
            </div>

            <button
              type="submit"
              disabled={!querySubject.trim()}
              className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold text-sm uppercase rounded-xl tracking-wider flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
            >
              <Search className="w-4 h-4" />
              <span>{isUk ? "Запустити AML-скринінг" : "Run AML Screening"}</span>
            </button>
          </form>

          {/* Quick Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-slate-100 pt-6">
            <div className="flex gap-2.5 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <Building className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <h4 className="text-[11px] font-bold text-slate-700">{isUk ? "ПЕРЕВІРКА ЄДРПОУ / РНОКПП" : "EDRPOU SCANNER"}</h4>
                <p className="text-[10px] text-slate-555 leading-normal">{isUk ? "Миттєве сканування реєстраційних кодів компаній." : "Scans company register identification codes."}</p>
              </div>
            </div>
            <div className="flex gap-2.5 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <UserCheck className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <h4 className="text-[11px] font-bold text-slate-700">{isUk ? "OSINT & БЕНЕФІЦІАРИ" : "OSINT & OWNERS"}</h4>
                <p className="text-[10px] text-slate-555 leading-normal">{isUk ? "Аналіз кінцевих власників на зв'язки з державою-агресором." : "Traces ultimate owners for aggressor ties."}</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Loading view */}
      {isAuditing && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center max-w-2xl mx-auto space-y-6 mt-12 shadow-xs text-slate-800">
          <div className="relative w-16 h-16 mx-auto">
            <div className="absolute inset-0 border-4 border-red-100 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-t-red-600 rounded-full animate-spin"></div>
            <ShieldAlert className="w-6 h-6 text-red-500 absolute inset-0 m-auto animate-pulse" />
          </div>

          <div className="space-y-2">
            <h3 className="text-base font-bold text-slate-850 animate-pulse">{isUk ? "Триває AML-перевірка..." : "Running compliance audit..."}</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              {isUk 
                ? "Мультиагентна система проводить аналіз. Це зазвичай займає до 30-45 секунд." 
                : "Multi-agent system is exploring the database. This usually takes 30-45 seconds."}
            </p>
          </div>

          {/* Current Step */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-red-600 animate-pulse max-w-lg mx-auto flex items-center justify-center gap-2">
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            <span>{isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}</span>
          </div>
        </div>
      )}

      {/* Error View */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3 max-w-2xl mx-auto mt-6">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-xs font-bold text-red-750">{isUk ? "Помилка перевірки" : "Screening Error"}</h4>
            <p className="text-xs text-red-600/90 leading-relaxed">{error}</p>
            <button 
              onClick={() => setError(null)}
              className="text-[10px] text-red-600 hover:text-red-750 font-bold uppercase tracking-wider pt-1 block cursor-pointer"
            >
              {isUk ? "Спробувати ще раз" : "Try again"}
            </button>
          </div>
        </div>
      )}

      {/* Report Result View */}
      {amlReport && !isAuditing && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-slate-50 border border-slate-200 p-4 rounded-xl shadow-xs text-slate-800">
            <div className="flex items-center gap-2.5">
              <FileText className="w-5 h-5 text-red-500" />
              <div>
                <h3 className="text-sm font-bold text-slate-900">{isUk ? "Звіт комплаєнс-аудиту Due Diligence" : "Due Diligence Compliance Report"}</h3>
                <p className="text-[10px] text-slate-500">
                  {isUk ? `Об'єкт перевірки: ${querySubject}` : `Entity checked: ${querySubject}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyReport}
                className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 hover:text-slate-900 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
              >
                {copiedReport ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedReport ? (isUk ? 'Скопійовано' : 'Copied') : (isUk ? 'Копіювати' : 'Copy')}</span>
              </button>
              <button
                onClick={handleDownloadWord}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>{isUk ? 'Зберегти у Word (.docx)' : 'Save to Word (.docx)'}</span>
              </button>
            </div>
          </div>

          {/* Markdown Viewer */}
          <div className="bg-white border border-slate-250 rounded-xl p-6 sm:p-8 overflow-y-auto max-h-[600px] max-w-none text-slate-800 leading-relaxed font-sans scrollbar-thin shadow-sm">
            <div className="space-y-4">
              {amlReport.split('\n').map((line, idx) => {
                const trimmed = line.trim();
                if (trimmed.startsWith('###')) {
                  return <h3 key={idx} className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-1.5 pt-3 uppercase tracking-wide">{trimmed.replace(/###/g, '')}</h3>;
                }
                if (trimmed.startsWith('##')) {
                  return <h2 key={idx} className="text-base font-bold text-red-800 border-b border-slate-150 pb-2 pt-5">{trimmed.replace(/##/g, '')}</h2>;
                }
                if (trimmed.startsWith('#')) {
                  return <h1 key={idx} className="text-lg font-black text-red-950 pb-3 pt-5 border-b border-slate-200">{trimmed.replace(/#/g, '')}</h1>;
                }
                if (trimmed.startsWith('🔴') || trimmed.startsWith('🟡') || trimmed.startsWith('🟢') || trimmed.startsWith('👉')) {
                  return <p key={idx} className="my-2 p-3 bg-slate-50 border border-slate-150 rounded-lg text-slate-800 font-medium flex gap-2 items-start">{line}</p>;
                }
                if (trimmed.startsWith('-') || trimmed.startsWith('*')) {
                  return <li key={idx} className="ml-4 list-disc text-slate-700 my-1">{line.replace(/^[-*]\s+/, '')}</li>;
                }
                return <p key={idx} className="my-1.5 text-[13px]">{line}</p>;
              })}
            </div>
          </div>

          <div className="flex justify-center pt-2">
            <button
              onClick={() => {
                setAmlReport(null);
                setQuerySubject('');
              }}
              className="px-4 py-2 border border-slate-200 hover:border-slate-300 bg-white text-slate-700 hover:text-slate-900 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>{isUk ? "Нова перевірка" : "Verify Another Subject"}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
